import React from 'react';
import { __ } from '../../Lang.js';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import VideoCard from './VideoCard.jsx';
import GroupCard from './GroupCard.jsx';

function SortableItem({ video, index, onDelete, isDraggingId }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: video.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
    opacity: isDragging ? 0.6 : 1,
    border: '2px solid #ff8c42',
    borderRadius: '8px',
    cursor: 'grab',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="video-grid__item video-grid__item--active"
      onClick={(e) => {
        if (isDraggingId) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      {...attributes}
      {...listeners}
    >
      <VideoCard
        key={video.uniqid}
        video={video}
        index={index}
        isEditinggroup={true}
        isDragging={isDraggingId === video.id}
        onDelete={() => onDelete(video.id)}
      />
    </div>
  );
}

const VideoGrid = ({
  videos,
  isEditinggroup = false,
  isEditingPlaylist = false,
  orderedVideos = [],
  setOrderedVideos = () => {},
  onReorder = null,
  onDeleteVideo = () => {},
  groups = [],
}) => {
  const [draggingId, setDraggingId] = React.useState(null);

  // Support both isEditinggroup and isEditingPlaylist prop names
  const isEditing = isEditinggroup || isEditingPlaylist;

  const displayedVideos = isEditing
    ? orderedVideos.map((id) => videos.find((v) => v.id === id)).filter(Boolean)
    : videos;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setDraggingId(null);
    if (active.id !== over?.id) {
      const oldIndex = orderedVideos.indexOf(active.id);
      const newIndex = orderedVideos.indexOf(over.id);
      const reordered = arrayMove(orderedVideos, oldIndex, newIndex);
      setOrderedVideos(reordered);
      // Persist immediately so the new order survives a page reload.
      // Previously the drag only updated local state; users assumed it was
      // saved and would navigate away, then return to find the original
      // order (the explicit Save button was the only persistence path and
      // was easy to miss). Parent passes a callback that PUTs to
      // /api/v2/twi/mylist/{uniqid}/reorder.
      if (typeof onReorder === 'function') {
        onReorder(reordered);
      }
    }
  };

  return (
    <div className="video-grid-container">
      {isEditing ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={({ active }) => setDraggingId(active.id)}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={orderedVideos} strategy={rectSortingStrategy}>
            <div className="video-grid">
              {displayedVideos.map((video, index) => (
                <SortableItem
                  key={video.id}
                  video={video}
                  index={index}
                  onDelete={onDeleteVideo}
                  isDraggingId={draggingId}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="video-grid">
          {(Array.isArray(groups) ? groups : Object.values(groups ?? {})).map((group) => (
            <div key={group.uniqid} className="video-grid__item">
              <GroupCard
                group={group}
              />
            </div>
          ))}
            {displayedVideos.map((video, index) => (
              <div key={index + '-' + video.uniqid} className="video-grid__item">
                <VideoCard
                  video={video}
                />
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default VideoGrid;